import * as Model from './index';
import axios from 'services/axios';
import React from 'react';
import { cloneDeep } from 'lodash';
import locationIcon from 'assets/location_icon.svg?react';
import * as ObservationApiCall from './ObservationIntegrationModel';
import * as MachineryApiCall from './MachineryIntegrationModel';
import PartnersLookup from './partner_module_lookup.json';
import { AxiosResponse } from 'axios';
import { PartnerId, USER_ORG_ID } from './Integration';
import { getPartnerOAuthSettings } from 'views/Integrations/utils/getPartnerOAuthSettings';

const TRAP_VIEW_API_URL = import.meta.env.VITE_APP_TRAP_VIEW_API_URL;
const ARABLE_API_URL = import.meta.env.VITE_APP_ARABLE_API_URL;
const SENCROP_API_URL = import.meta.env.VITE_APP_SENCROP_API_URL;
const GROGURU_API_URL = import.meta.env.VITE_APP_GROGURU_API_URL;
const VALENCO_AUTH_URL = import.meta.env.VITE_APP_VALENCO_AUTH_URL;

const IntegrationPartnerCommonProperties: any = {
  get_settings_values: (params: Model.WizardCallParams) => {
    const { settings } = params;
    if (settings && Object.keys(settings).includes('devices')) {
      return settings.devices;
    }
    return null;
  },
  update_settings: (params: Model.WizardCallParams) => {
    const { settings, wizardData } = params;
    let clonedSettings = cloneDeep(settings);
    if (!wizardData.selectedItems) {
      return clonedSettings;
    }

    if (clonedSettings) {
      clonedSettings.devices = wizardData.selectedItems;
    } else {
      return {
        devices: wizardData.selectedItems,
      };
    }
    return clonedSettings;
  },
  delete_settings: (params: Model.WizardCallParams) => {
    const { settings } = params;
    if (settings && settings.devices) {
      settings.devices = [];
    }
    return settings;
  },
};

const IntegrationPartnerProperties: any = {
  [PartnerId.TrapView]: {
    username_label: PartnersLookup.trap_view.basicAuthLabels.username_label,
    password_label: PartnersLookup.trap_view.basicAuthLabels.password_label,
    validate_credential: async (userName: string, password: string) => {
      const authToken = btoa(`${userName}:${password}`);
      const response = await axios.request({
        method: 'GET',
        url: `${TRAP_VIEW_API_URL}/getUser/${userName}`,
        headers: { authorization: `Basic ${authToken}` },
        data: {
          user: {
            idSubject: 'idSubject',
          },
        },
      });
      return {
        access_token: response.data.user.idSubject,
        auth_config: {
          username: userName,
          password: password,
        },
      };
    },
    get_devices: async (params: Model.WizardCallParams) => {
      const { accessToken, wizardData } = params;
      const { auth_config } = wizardData;
      const { username, password } = auth_config.auth_config;
      if (accessToken) {
        return {
          items: await ObservationApiCall.getTrapViewDevices(accessToken, username, password),
        };
      } else {
        return Promise.reject('No access token');
      }
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter(
        (_item) =>
          `${_item.id}-${_item.name}${_item.partNo}`.toLowerCase().search(value.toLowerCase()) > -1,
      );
    },
    get_tile_title: (item: any, _type: string) => {
      return `${item.id}-${item.name}`;
    },
    get_tile_subtitle: (item: any, _type: string) => {
      return item.partNo;
    },
    get_tile_icon: (_item: any, _type: string) => {
      return locationIcon;
    },
  },
  [PartnerId.Arable]: {
    username_label: PartnersLookup.arable.basicAuthLabels.username_label,
    password_label: PartnersLookup.arable.basicAuthLabels.password_label,
    validate_credential: async (userName: string, password: string) => {
      const response: any = await axios.request({
        method: 'POST',
        url: `${ARABLE_API_URL}/auth/token`,
        data: {
          email: userName,
          password: password,
        },
      });
      return {
        access_token: response.data?.token,
        auth_config: {
          access_token: response.data?.token,
        },
      };
    },
    get_devices: async (params: Model.WizardCallParams) => {
      const { wizardData } = params;
      const response: any = await ObservationApiCall.getAllArableDevices(wizardData.user_id);
      if (response?.items) {
        return {
          items: response.items.map((_device: Model.ArableDevice) => {
            _device.orgId = _device.id;
            _device.id = _device.name;
            return _device;
          }),
        };
      } else {
        return Promise.reject('No devices');
      }
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter(
        (_item) =>
          `${_item.name}${_item.current_location.name}`.toLowerCase().search(value.toLowerCase()) >
          -1,
      );
    },
    get_tile_title: (item: any, _type: string) => {
      return item.name;
    },
    get_tile_subtitle: (item: any, _type: string) => {
      return item.current_location.name;
    },
    get_tile_icon: (_item: any, _type: string) => {
      return locationIcon;
    },
  },
  [PartnerId.Sencrop]: {
    username_label: PartnersLookup.sencrop.basicAuthLabels.username_label,
    password_label: PartnersLookup.sencrop.basicAuthLabels.password_label,
    auth_hint: PartnersLookup.sencrop.basicAuthLabels.auth_hint,
    validate_credential: async (userName: string, password: string) => {
      const response = await axios.request({
        method: 'POST',
        url: `${SENCROP_API_URL}/oauth2/token`,
        auth: {
          username: userName,
          password: password,
        },
        data: {
          application_id: userName,
          application_secret: password,
          grant_type: 'client_credentials',
          scope: 'user',
          access_token: 'access_token',
        },
      });
      return {
        access_token: response.data.access_token,
        auth_config: {
          application_id: userName,
          application_secret: password,
        },
      };
    },
    get_organizations: async (params: Model.WizardCallParams) => {
      try {
        const { accessToken } = params;
        const response: any = await ObservationApiCall.getSencropUsersPartners(accessToken);
        if (response?.users && response?.organisations && Object.keys(response.users)?.length) {
          return {
            wizardData: {
              user: response.users[Object.keys(response.users)[0]].contents?.email,
            },
            items: Object.values(response.organisations),
          };
        }
        return Promise.reject('No organizations found');
      } catch (error: any) {
        console.error(error);
        return Promise.reject(error);
      }
    },
    get_devices: async (params: Model.WizardCallParams) => {
      const { accessToken, settings } = params;
      const organizations = settings?.users[0]?.partners;
      let devicesRequests: any[] = [];
      if (organizations?.length) {
        organizations.forEach((orgId: number) => {
          devicesRequests.push(ObservationApiCall.getSencropPartnerDevices(accessToken, orgId));
        });
        const response = await Promise.allSettled(devicesRequests);
        let devicesObj: any[] = [];
        if (response && response.length) {
          response.forEach((res: any) => {
            if (res.status === 'fulfilled' && res.value && !res.value.error_type) {
              devicesObj.push(
                ...Object.values(res.value.devices).map((device: any) => {
                  device.model = res.value.models[device.modelId];
                  device.partner = organizations.find(
                    (_org: any) => _org === device.accessPeriods[0]?.organisationId,
                  );
                  return device;
                }),
              );
            } else if (res.status === 'rejected') {
              console.error(res);
            }
          });
        }
        return {
          items: devicesObj,
        };
      } else {
        return Promise.reject('No partners');
      }
    },
    search_items: (value: string, stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      switch (stepType) {
        case Model.WizardTypes.ORGANIZATIONS:
          return items.filter(
            (_item) =>
              `${_item.contents?.name}${_item.id}`.toLowerCase().search(value.toLowerCase()) > -1,
          );
        case Model.WizardTypes.DEVICES:
          return items.filter(
            (_item) =>
              `${_item.id}-${_item.serial}${_item.partner}`
                .toLowerCase()
                .search(value.toLowerCase()) > -1,
          );
      }
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings, step } = params;
      if (settings && settings.users?.length) {
        switch (step.type) {
          case Model.WizardTypes.ORGANIZATIONS:
            if (settings.users[0].partners?.length > 0) {
              return settings.users[0].partners;
            }
            break;
          case Model.WizardTypes.DEVICES:
            return settings.users[0].devices;
        }
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { step, settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems && !wizardData.user) {
        return clonedSettings;
      }
      if (clonedSettings) {
        let userObj: any = { email: wizardData.user };
        if (clonedSettings.users?.length) {
          userObj = {
            ...clonedSettings.users[0],
            ...userObj,
          };
        }
        switch (step.type) {
          case Model.WizardTypes.ORGANIZATIONS:
            userObj = {
              ...userObj,
              partners: wizardData.selectedItems,
            };
            break;
          case Model.WizardTypes.DEVICES:
            userObj = {
              ...userObj,
              devices: wizardData.selectedItems,
            };
            break;
        }
        clonedSettings.users = [userObj];
        return clonedSettings;
      }
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData, step } = params;
      if (settings?.users?.length) {
        settings.users[0].devices = [];
        if (step.type === Model.WizardTypes.ORGANIZATIONS && wizardData.selectedItems?.length) {
          settings.users[0].partners = [...wizardData.selectedItems];
        }
      }
      return settings;
    },
    get_tile_title: (item: any, type: string) => {
      switch (type) {
        case Model.WizardTypes.ORGANIZATIONS:
          return item?.contents?.name;
        case Model.WizardTypes.DEVICES:
          return `${item.id}-${item.serial}`;
        default:
          return '';
      }
    },
    get_tile_subtitle: (item: any, type: string) => {
      switch (type) {
        case Model.WizardTypes.ORGANIZATIONS:
          return item?.id;
        case Model.WizardTypes.DEVICES:
          return item?.partner;
        default:
          return '';
      }
    },
    get_tile_icon: (_item: any, type: string) => {
      switch (type) {
        case Model.WizardTypes.ORGANIZATIONS:
          return null;
        case Model.WizardTypes.DEVICES:
          return locationIcon;
        default:
          return null;
      }
    },
  },
  [PartnerId.GroGuru]: {
    username_label: PartnersLookup.groguru.basicAuthLabels.username_label,
    password_label: PartnersLookup.groguru.basicAuthLabels.password_label,
    validate_credential: async (userName: string, password: string) => {
      const response = await axios.request({
        method: 'POST',
        url: `${GROGURU_API_URL}/user/signin`,
        data: {
          email: userName,
          password: password,
          data: {
            id: 'id',
            sessionToken: 'sessionToken',
          },
        },
      });
      return {
        access_token: {
          id: response.data.data.id,
          sessionToken: response.data.data.sessionToken,
        },
        auth_config: {
          username: userName,
          password: password,
        },
      };
    },
    get_sites: async (params: Model.WizardCallParams) => {
      const { accessToken } = params;
      return {
        items: await ObservationApiCall.getGroGuruSites(accessToken),
      };
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter(
        (_item) => `${_item.siteId}${_item.farm}`.toLowerCase().search(value.toLowerCase()) > -1,
      );
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && Object.keys(settings).includes('sites')) {
        return settings.sites;
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems) {
        return clonedSettings;
      }
      if (clonedSettings) {
        clonedSettings.sites = wizardData.selectedItems;
      } else {
        return {
          sites: wizardData.selectedItems,
        };
      }
      return clonedSettings;
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && settings.sites) {
        settings.sites = [];
      }
      return settings;
    },
    get_tile_title: (item: any, _type: string) => {
      return item.siteId;
    },
    get_tile_subtitle: (item: any, _type: string) => {
      return item.farm;
    },
    get_tile_icon: (_item: any, _type: string) => {
      return locationIcon;
    },
  },
  [PartnerId.Veris]: {
    username_label: PartnersLookup.veris.basicAuthLabels.username_label,
    password_label: PartnersLookup.veris.basicAuthLabels.password_label,
    validate_credential: async (userName: string, password: string) => {
      const user_org_id = window.localStorage.getItem(USER_ORG_ID);
      if (user_org_id) {
        await axios.request({
          method: 'POST',
          url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/veris-login`,
          data: {
            user_id: user_org_id.split('_')[0],
            org_id: user_org_id.split('_')[1],
            username: userName,
            password: password,
          },
        });
        return {
          access_token: {
            token: user_org_id,
          },
          auth_config: {
            username: userName,
            password: password,
          },
        };
      }
      return Promise.reject('user/org id is missing');
    },
    get_users: async (params: Model.WizardCallParams) => {
      const { accessToken } = params;
      const response: any = await ObservationApiCall.getVerisUsers(accessToken.token);
      return {
        items: response.ChildUsers?.length
          ? response.ChildUsers.map((_user: Model.VerisChildUser) => {
              _user.id = _user.Username;
              return _user;
            })
          : [],
      };
    },
    get_farms: async (params: Model.WizardCallParams) => {
      const { accessToken, settings, wizardData } = params;
      const selectedItems = wizardData.selectedItems?.length
        ? wizardData.selectedItems
        : settings?.filter.map((_item: Model.VerisFarmAuthAccount) => _item.grower);
      const response: any = await ObservationApiCall.getVerisFarms(
        accessToken.token,
        selectedItems,
      );
      return {
        wizardData: {
          ...wizardData,
          farms: response,
        },
        items: response,
      };
    },
    search_items: (value: string, stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      switch (stepType) {
        case Model.WizardTypes.USERS:
          return items.filter(
            (_item) =>
              `${_item.Username}${_item.Info?.Name}`.toLowerCase().search(value.toLowerCase()) > -1,
          );
        case Model.WizardTypes.FARMS:
          return items.filter(
            (_item) => `${_item.id}${_item.grower}`.toLowerCase().search(value.toLowerCase()) > -1,
          );
      }
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings, step } = params;
      if (settings && settings.filter?.length) {
        switch (step.type) {
          case Model.WizardTypes.USERS:
            let users: string[] = [];
            settings.filter.forEach((_filter: Model.VerisFilterSetting) =>
              users.push(_filter.grower),
            );
            return users;
          case Model.WizardTypes.FARMS:
            let farms: string[] | null = [];
            settings.filter.forEach((_filter: Model.VerisFilterSetting) => {
              if (farms && _filter.farms?.length) {
                farms.push(..._filter.farms);
              } else {
                farms = null;
              }
            });
            return farms;
        }
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { step, settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems) {
        return clonedSettings;
      }
      if (clonedSettings) {
        switch (step.type) {
          case Model.WizardTypes.USERS:
            clonedSettings.filter = wizardData.selectedItems?.map((_item: string) => {
              let existingFarms: any = clonedSettings?.filter?.find(
                (_verisFarms: Model.VerisFarmAuthAccount) => _verisFarms.grower === _item,
              );
              return {
                grower: _item,
                farms: existingFarms?.farms ?? null,
              };
            });
            break;
          case Model.WizardTypes.FARMS:
            clonedSettings.filter = ObservationApiCall.generateVerisSettings(
              clonedSettings.filter,
              wizardData.farms,
              wizardData.selectedItems,
            );
            break;
        }
      }
      return clonedSettings;
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData, step } = params;
      if (!settings) return settings;
      if (step.type === Model.WizardTypes.USERS && wizardData.selectedItems?.length) {
        settings.filter = wizardData.selectedItems.map((_grower: string) => {
          return {
            grower: _grower,
            farms: [],
          };
        });
      } else {
        settings.filter = settings.filter.map((_filter: Model.VerisFarmAuthAccount) => {
          return {
            grower: _filter.grower,
            farms: [],
          };
        });
      }
      return settings;
    },
    get_tile_title: (item: any, type: string) => {
      switch (type) {
        case Model.WizardTypes.USERS:
          return item.id;
        case Model.WizardTypes.FARMS:
          return item.id;
        default:
          return '';
      }
    },
    get_tile_subtitle: (item: any, type: string) => {
      switch (type) {
        case Model.WizardTypes.USERS:
          return item?.Info?.Name;
        case Model.WizardTypes.FARMS:
          return item.grower;
        default:
          return '';
      }
    },
    get_tile_icon: (_item: any, _type: string) => {
      return null;
    },
  },
  [PartnerId.Pessl]: {
    get_access_token: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code: string | null = urlParams.get('code') ? urlParams.get('code') : null;
      const user_org_id = window.localStorage.getItem(USER_ORG_ID);
      if (code && user_org_id) {
        const pesslSettings = getPartnerOAuthSettings(PartnerId.Pessl);
        const response: AxiosResponse<Model.AuthAccountResponse> = await axios.request({
          method: 'POST',
          url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/pessl-auth-code`,
          data: {
            user_id: user_org_id.split('_')[0],
            org_id: user_org_id.split('_')[1],
            code: code,
            redirect_uri: pesslSettings?.params?.redirect_uri,
          },
        });
        return {
          token: {
            access_token: response.data?.auth_config.access_token,
            auth_config: {
              ...response.data?.auth_config,
            },
          },
          settings: {
            ...response.data?.settings,
          },
        };
      }
      return Promise.reject('Code or user is not valid');
    },
    get_stations: async (params: Model.WizardCallParams) => {
      const { accessToken } = params;
      const response: any = await ObservationApiCall.getPesslStations(accessToken);
      return {
        items: response.map((station: Model.PesslStation) => {
          return {
            id: station.name.original,
          };
        }),
      };
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter((_item) => `${_item.id}`.toLowerCase().search(value.toLowerCase()) > -1);
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && Object.keys(settings).includes('stations')) {
        return settings.stations;
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems) {
        return clonedSettings;
      }
      if (clonedSettings) {
        clonedSettings.stations = wizardData.selectedItems;
      } else {
        return {
          stations: wizardData.selectedItems,
        };
      }
      return clonedSettings;
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && settings.stations) {
        settings.stations = [];
      }
      return settings;
    },
    get_tile_title: (_item: any, _type: string) => {
      return _item.id;
    },
    get_tile_subtitle: (_item: any, _type: string) => {
      return null;
    },
    get_tile_icon: (_item: any, _type: string) => {
      return locationIcon;
    },
  },
  [PartnerId.Valenco]: {
    username_label: PartnersLookup.valenco.basicAuthLabels.username_label,
    password_label: PartnersLookup.valenco.basicAuthLabels.password_label,
    validate_credential: async (userName: string, password: string) => {
      const response: any = await axios.request({
        method: 'POST',
        url: VALENCO_AUTH_URL,
        data: {
          username: userName,
          password: password,
        },
      });
      return {
        access_token: response.data?.token,
        auth_config: {
          username: userName,
          password: password,
        },
      };
    },
    get_devices: async (params: Model.WizardCallParams) => {
      const { accessToken } = params;
      const response: any = await ObservationApiCall.getAllValencoDevices(accessToken);
      if (response) {
        return {
          items: response.map((_device: any) => {
            _device.id = _device.id.id;
            return _device;
          }),
        };
      } else {
        return Promise.reject('No devices');
      }
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter((_item) => `${_item.id}`.toLowerCase().search(value.toLowerCase()) > -1);
    },
    get_tile_title: (_item: any, _type: string) => {
      return _item.id;
    },
    get_tile_subtitle: (_item: any, _type: string) => {
      return null;
    },
    get_tile_icon: (_item: any, _type: string) => {
      return null;
    },
  },
  [PartnerId.AgFiniti]: {
    get_access_token: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code: string | null = urlParams.get('code');
      const user_org_id = window.localStorage.getItem(USER_ORG_ID);
      if (code && user_org_id) {
        const pesslSettings = getPartnerOAuthSettings(PartnerId.AgFiniti);
        const response: AxiosResponse<Model.AuthAccountResponse> = await axios.request({
          method: 'POST',
          url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/agfiniti-auth-code`,
          data: {
            user_id: user_org_id.split('_')[0],
            org_id: user_org_id.split('_')[1],
            code: code,
            redirect_uri: pesslSettings?.params?.redirect_uri,
          },
        });
        return {
          token: {
            access_token: response.data?.auth_config.access_token,
            auth_config: {
              ...response.data?.auth_config,
            },
          },
          settings: {
            ...response.data?.settings,
          },
        };
      }
      return Promise.reject('Code or user is not valid');
    },
    get_growers: async (params: Model.WizardCallParams) => {
      const { settings, wizardData } = params;
      if (wizardData?.user_id && settings?.start_time) {
        const response = await MachineryApiCall.getAgFinitiGrowers(
          wizardData.user_id,
          settings.start_time,
        );
        return {
          items: response.growers.map((_grower: string) => {
            return {
              id: _grower,
            };
          }),
          wizardData: {
            ...wizardData,
            all_growers: [...response.growers],
          },
        };
      } else {
        return Promise.reject('No Start Date');
      }
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter((_item) => `${_item.id}`.toLowerCase().search(value.toLowerCase()) > -1);
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && Object.keys(settings).includes('selected_growers')) {
        return settings.selected_growers;
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems) {
        return clonedSettings;
      }
      if (clonedSettings) {
        clonedSettings.selected_growers = wizardData.selectedItems;
        clonedSettings.all_growers = wizardData.all_growers;
      } else {
        return {
          selected_growers: wizardData.selectedItems,
          all_growers: wizardData.all_growers,
        };
      }
      return clonedSettings;
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && settings.selected_growers) {
        settings.selected_growers = [];
      }
      return settings;
    },
    get_tile_title: (_item: any, _type: string) => {
      return _item.id;
    },
  },
  [PartnerId.Farmobile]: {
    get_access_token: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code: string | null = urlParams.get('code') ? urlParams.get('code') : null;
      const user_org_id = window.localStorage.getItem(USER_ORG_ID);
      if (code && user_org_id) {
        const response: AxiosResponse<Model.AuthAccountResponse> = await axios.request({
          method: 'POST',
          url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/farmobile-auth-code`,
          data: {
            user_id: user_org_id.split('_')[0],
            org_id: user_org_id.split('_')[1],
            code: code,
          },
        });
        return {
          token: {
            access_token: response.data?.auth_config.access_token,
            auth_config: {
              ...response.data?.auth_config,
            },
          },
          settings: {
            ...response.data?.settings,
          },
        };
      }
      return Promise.reject('Code or user is not valid');
    },
    get_organizations: async (params: Model.WizardCallParams) => {
      const { wizardData } = params;
      const response = await MachineryApiCall.getFarmobileOrganizations(wizardData.user_id);
      return {
        items: response.map((_org: Model.FarmobileOrganizations) => {
          return {
            id: _org.company_fmid,
            name: _org.name,
          };
        }),
        wizardData: {
          ...wizardData,
          all_organizations: [
            ...response.map((_org: Model.FarmobileOrganizations) => _org.company_fmid),
          ],
        },
      };
    },
    search_items: (value: string, _stepType: Model.WizardTypes | undefined, items: any[]) => {
      if (value === '' || !value) {
        return items;
      }
      return items.filter(
        (_item) => `${_item.id}${_item.name}`.toLowerCase().search(value.toLowerCase()) > -1,
      );
    },
    get_settings_values: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && Object.keys(settings).includes('selected_organizations')) {
        return settings.selected_organizations;
      }
      return null;
    },
    update_settings: (params: Model.WizardCallParams) => {
      const { settings, wizardData } = params;
      let clonedSettings = cloneDeep(settings);
      if (!wizardData.selectedItems) {
        return clonedSettings;
      }
      if (clonedSettings) {
        clonedSettings.selected_organizations = wizardData.selectedItems;
        clonedSettings.all_organizations = wizardData.all_organizations;
      } else {
        return {
          selected_organizations: wizardData.selectedItems,
          all_organizations: wizardData.all_organizations,
        };
      }
      return clonedSettings;
    },
    delete_settings: (params: Model.WizardCallParams) => {
      const { settings } = params;
      if (settings && settings.selected_organizations) {
        settings.selected_organizations = [];
      }
      return settings;
    },
    get_tile_title: (_item: any, _type: string) => {
      return _item.name;
    },
    get_tile_subtitle: (_item: any, _type: string) => {
      return _item.id;
    },
  },
};
class PartnerModule extends React.Component {
  static get_username_label(partner_id: string) {
    return IntegrationPartnerProperties[partner_id]['username_label'];
  }
  static get_password_label(partner_id: string) {
    return IntegrationPartnerProperties[partner_id]['password_label'];
  }
  static get_auth_hint(partner_id: string) {
    return IntegrationPartnerProperties[partner_id]['auth_hint']
      ? IntegrationPartnerProperties[partner_id]['auth_hint']
      : null;
  }
  static get_validation_token(partner_id: string, userName: string, password: string) {
    return IntegrationPartnerProperties[partner_id]['validate_credential'](userName, password);
  }
  static get_access_token(partner_id: string) {
    return IntegrationPartnerProperties[partner_id]['get_access_token']
      ? IntegrationPartnerProperties[partner_id]['get_access_token']()
      : null;
  }
  static get_list_items(params: Model.WizardCallParams) {
    const { partnerId, step } = params;
    if (step.callFunction) {
      return IntegrationPartnerProperties[partnerId][step.callFunction]
        ? IntegrationPartnerProperties[partnerId][step.callFunction](params)
        : null;
    }
    return null;
  }
  static get_settings_values(params: Model.WizardCallParams) {
    const { partnerId } = params;
    return IntegrationPartnerProperties[partnerId]['get_settings_values']
      ? IntegrationPartnerProperties[partnerId]['get_settings_values'](params)
      : IntegrationPartnerCommonProperties['get_settings_values'](params);
  }
  static update_settings(params: Model.WizardCallParams) {
    const { partnerId, action } = params;
    if (action === Model.SETTINGS_ACTION.UPDATE) {
      return IntegrationPartnerProperties[partnerId]['update_settings']
        ? IntegrationPartnerProperties[partnerId]['update_settings'](params)
        : IntegrationPartnerCommonProperties['update_settings'](params);
    } else if (action === Model.SETTINGS_ACTION.DELETE) {
      return IntegrationPartnerProperties[partnerId]['delete_settings']
        ? IntegrationPartnerProperties[partnerId]['delete_settings'](params)
        : IntegrationPartnerCommonProperties['delete_settings'](params);
    }
  }
  static get_tile_title(partnerId: string, item: any, type: string | undefined) {
    return IntegrationPartnerProperties[partnerId]['get_tile_title']
      ? IntegrationPartnerProperties[partnerId]['get_tile_title'](item, type)
      : null;
  }
  static get_tile_subtitle(partnerId: string, item: any, type: string | undefined) {
    return IntegrationPartnerProperties[partnerId]['get_tile_subtitle']
      ? IntegrationPartnerProperties[partnerId]['get_tile_subtitle'](item, type)
      : null;
  }
  static get_tile_icon(partnerId: string, item: any, type: string | undefined) {
    return IntegrationPartnerProperties[partnerId]['get_tile_icon']
      ? IntegrationPartnerProperties[partnerId]['get_tile_icon'](item, type)
      : null;
  }
  static search_items(
    partner_id: string,
    stepType: Model.WizardTypes | undefined,
    value: string,
    lists: any[],
  ) {
    return IntegrationPartnerProperties[partner_id]['search_items']
      ? IntegrationPartnerProperties[partner_id]['search_items'](value, stepType, lists)
      : [];
  }
}
export default PartnerModule;
